// **Live mode's per-type counts, in the shape `TypeSheet` reads.**
//
// `TypeSheet` was written for the rounds sandbox, where a chip's number comes off the resident
// trace record (`ui/rounds/type-counts.ts`'s `PlayheadPopulation`). Live mode has the same figure
// from a different wire shape — `StateHistogram`, a per-type list of per-state buckets — so the
// pvp Colony screen adapts rather than `TypeSheet` growing a second input.
//
// The two rules `type-counts.ts` states are kept verbatim, because they are what make the
// readouts honest:
//
//   * **Absence is a placeholder, never a retained value.** No histogram at all — the window
//     before the first one arrives — is `POPULATION_UNAVAILABLE`, not an empty map, so the sheet
//     renders `—` rather than a fabricated zero.
//   * **The queen is excluded**, which she already is here: she has no `typeId` and therefore no
//     `TypeHistogram` row, so nothing has to drop her.
//
// A type present with every bucket at zero is a real `0` — it has died out — and is kept in the
// map so `countForType` reads it as the zero it is.

import type { StateHistogram } from '../../net/wire/messages';
import { POPULATION_UNAVAILABLE, type PlayheadPopulation } from '../rounds/type-counts';

/** The living, non-queen population from a live `StateHistogram`. `null` in, placeholder out. */
export function populationFromHistogram(histogram: StateHistogram | null): PlayheadPopulation {
  if (histogram === null) {
    return POPULATION_UNAVAILABLE;
  }
  const byType = new Map<number, number>();
  let total = 0;
  for (const perType of histogram.perType) {
    const count = perType.buckets.reduce((sum, bucket) => sum + bucket.count, 0);
    byType.set(perType.typeId, (byType.get(perType.typeId) ?? 0) + count);
    total += count;
  }
  return { available: true, byType, total };
}
